import React, { useState } from "react";

const Python = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const syllabusData = {
    courseOverview: {
      title: "Course Overview",
      content: (
        <>
          <p className="font-bold text-sm">Objective:</p> Learn Python
          programming from basics to intermediate level, focusing on practical
          applications.
          <p className="font-bold text-sm">Duration:</p> 30 days, 1 hour per
          day.
          <p className="font-bold text-sm">Prerequisites:</p> No prior
          programming experience required; basic computer literacy is helpful.
          <p className="font-bold text-sm">Tools Needed:</p> Python 3.x, a code
          editor (e.g., VS Code, PyCharm), and a computer with internet access.
        </>
      ),
    },
    dailySchedule: {
      title: "Daily Schedule",
      days: [
        {
          day: "Day 1: Introduction to Python",
          objective:
            "Understand Python and set up the development environment.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is Python? Why use it?</li>
                <li>Python history and features.</li>
                <li>Installing Python and setting up a code editor.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Install Python and print "Hello World".</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 2: Python Basics – Variables and Data Types",
          objective: "Learn about variables, data types, and basic operations.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Variables and naming conventions.</li>
                <li>Basic data types: int, float, str, bool.</li>
                <li>Type conversion and basic operations.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create variables and perform arithmetic operations.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 3: Strings and String Operations",
          objective: "Understand strings and common string operations.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>String creation and manipulation.</li>
                <li>String methods (e.g., upper(), lower(), strip()).</li>
                <li>String formatting (f-strings, .format()).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write a program to manipulate strings.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 4: Control Flow – Conditionals",
          objective: "Learn to use conditional statements in Python.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>if, elif, and else statements.</li>
                <li>Comparison and logical operators.</li>
                <li>Nested conditionals.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Create a program to determine if a number is even or odd.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 5: Control Flow – Loops",
          objective: "Understand loops for iteration in Python.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>for loops and while loops.</li>
                <li>Loop control statements (break, continue).</li>
                <li>Nested loops.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Print a multiplication table using loops.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 6: Lists and List Operations",
          objective: "Learn how to work with lists in Python.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Creating and modifying lists.</li>
                <li>List methods (append, remove, pop, etc.).</li>
                <li>List slicing and indexing.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write a program to manage a to-do list.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 7: Tuples and Sets",
          objective: "Understand tuples and sets in Python.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Tuples: creation, immutability, and use cases.</li>
                <li>Sets: creation, operations (union, intersection).</li>
                <li>Comparing lists, tuples, and sets.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Remove duplicates from a list using sets.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 8: Dictionaries",
          objective: "Learn to use dictionaries for key-value data.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Creating and accessing dictionaries.</li>
                <li>Dictionary methods (get, keys, values).</li>
                <li>Nested dictionaries.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build a simple contact book using dictionaries.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 9: Functions – Basics",
          objective: "Understand how to define and use functions.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Defining functions with def.</li>
                <li>Function arguments and return statements.</li>
                <li>Default and keyword arguments.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write a function to calculate the area of a rectangle.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 10: Functions – Advanced",
          objective: "Explore advanced function concepts.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Lambda functions.</li>
                <li>Scope and global/local variables.</li>
                <li>Recursive functions.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Calculate factorial using recursion.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 11: Working with Modules",
          objective: "Learn how to use modules and packages.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Importing built-in modules (math, random, datetime).</li>
                <li>Creating custom modules.</li>
                <li>Installing external packages with pip.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Use the random module to create a dice-rolling program.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 12: File Handling – Basics",
          objective: "Understand how to read and write files.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Opening and closing files.</li>
                <li>Reading from and writing to files.</li>
                <li>File modes (read, write, append).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write a program to save and read user data from a file.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 13: File Handling – Advanced",
          objective: "Explore advanced file handling techniques.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Working with CSV files.</li>
                <li>Using the with statement for file handling.</li>
                <li>Handling file exceptions.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a program to read and write CSV data.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 14: Exception Handling",
          objective: "Learn to handle errors and exceptions.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>try, except, else, and finally blocks.</li>
                <li>Common exceptions (e.g., ZeroDivisionError).</li>
                <li>Raising custom exceptions.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write a program to handle division by zero errors.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 15: Introduction to Object-Oriented Programming",
          objective: "Understand the basics of OOP in Python.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is OOP? Classes and objects.</li>
                <li>Creating a class and instantiating objects.</li>
                <li>Attributes and methods.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Create a class to represent a Car with attributes like make
                  and model.
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 16: OOP – Inheritance",
          objective: "Learn how to use inheritance in Python.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Inheriting from a parent class.</li>
                <li>Overriding methods.</li>
                <li>Using super() to call parent methods.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a Vehicle parent class and a Car child class.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 17: OOP – Encapsulation and Polymorphism",
          objective: "Explore encapsulation and polymorphism.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Private and protected attributes.</li>
                <li>Getter and setter methods.</li>
                <li>Polymorphism with method overriding.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Implement encapsulation in the Car class.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 18: Working with Libraries – NumPy Basics",
          objective: "Learn to use NumPy for numerical operations.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to NumPy.</li>
                <li>Creating arrays and basic operations.</li>
                <li>Array indexing and slicing.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Perform basic array operations with NumPy.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 19: Working with Libraries – Pandas Basics",
          objective: "Understand Pandas for data manipulation.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Pandas.</li>
                <li>Creating Series and DataFrames.</li>
                <li>Basic data operations (filtering, sorting).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Load and manipulate a dataset using Pandas.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 20: Data Visualization with Matplotlib",
          objective: "Learn to create visualizations with Matplotlib.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Matplotlib.</li>
                <li>Creating line plots, bar charts, and scatter plots.</li>
                <li>Customizing plots (labels, titles, colors).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Plot a dataset using Matplotlib.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 21: Working with APIs",
          objective: "Learn to fetch data from APIs using Python.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is an API?</li>
                <li>Using the requests library.</li>
                <li>Parsing JSON responses.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>
                  Fetch and display data from a public API (e.g., weather API).
                </li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 22: Introduction to Databases – SQLite",
          objective: "Learn to use SQLite with Python.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to databases and SQLite.</li>
                <li>Creating and connecting to a database.</li>
                <li>Basic SQL queries (CREATE, INSERT, SELECT).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a database to store user data.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 23: Database Operations – CRUD",
          objective: "Perform CRUD operations with SQLite.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Inserting, updating, and deleting records.</li>
                <li>Querying data with SELECT.</li>
                <li>Using parameters for safe queries.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build a simple CRUD application for a to-do list.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 24: Introduction to Web Scraping",
          objective: "Learn the basics of web scraping with Python.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is web scraping?</li>
                <li>Using BeautifulSoup and requests.</li>
                <li>Ethical considerations in web scraping.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Scrape data from a simple webpage.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 25: Automation with Python",
          objective: "Automate tasks using Python scripts.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Automating file management with os and shutil.</li>
                <li>Scheduling tasks with schedule.</li>
                <li>Automating email sending with smtplib.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write a script to organize files by extension.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 26: Testing in Python",
          objective: "Learn to write and run tests in Python.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to unit testing.</li>
                <li>Using the unittest framework.</li>
                <li>Writing and running tests.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write tests for a simple function.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 27: Introduction to GUI with Tkinter",
          objective: "Create a basic GUI application with Tkinter.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Tkinter.</li>
                <li>Creating windows, labels, and buttons.</li>
                <li>Handling user input.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build a simple calculator GUI.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 28: Final Project – Planning",
          objective: "Plan the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min discussion):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Overview of the final project.</li>
                <li>
                  Suggested project ideas (e.g., Expense Tracker, Weather App).
                </li>
                <li>Planning features and structure.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a project plan and outline.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 29: Final Project – Development",
          objective: "Develop the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Implementing core features.</li>
                <li>
                  Integrating learned concepts (e.g., file handling, APIs).
                </li>
                <li>Testing and debugging.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build the final project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 30: Final Project – Presentation",
          objective: "Present the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Preparing a demo.</li>
                <li>Discussing challenges and solutions.</li>
                <li>Presenting to peers.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Present the completed project.</li>
              </ul>
            </>
          ),
        },
      ],
    },
    learningOutcomes: {
      title: "Learning Outcomes",
      content: (
        <>
          <p className="text-sm">
            By the end of this course, learners will be able to:
          </p>
          <ul className="list-disc pl-4 text-xs">
            <li>Write Python programs to solve real-world problems.</li>
            <li>Work with data structures, files, and databases.</li>
            <li>
              Use libraries like NumPy, Pandas, and Matplotlib for data
              analysis.
            </li>
            <li>Automate tasks and build GUI applications.</li>
          </ul>
        </>
      ),
    },
    finalProjectIdeas: {
      title: "Suggested Final Project Ideas",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>
              <span className="font-bold text-sm">Expense Tracker:</span> A
              program to track income and expenses with data visualization.
            </li>
            <li>
              <span className="font-bold text-sm">Weather App:</span> An app
              that fetches and displays weather data using an API.
            </li>
            <li>
              <span className="font-bold text-sm">To-Do List Manager:</span> A
              GUI-based to-do list app with database storage.
            </li>
          </ul>
        </>
      ),
    },
    additionalNotes: {
      title: "Additional Notes",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>
              Encourage practice with coding challenges (e.g., on HackerRank,
              LeetCode).
            </li>
            <li>Provide resources (e.g., Python documentation, tutorials).</li>
            <li>Use discussion time for Q&A and clarifications.</li>
          </ul>
          <p className="text-sm">
            This syllabus progresses from basics to intermediate topics,
            ensuring practical skills in Python programming over 30 days.
          </p>
        </>
      ),
    },
  };

  // Function to handle enrollment (replace with actual enrollment logic)
  const handleEnroll = () => {
    alert("Enroll Now clicked! (Add your enrollment logic here)");
  };

  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      {/* Hero Section */}
      <header className="bg-gradient-to-r from-gray-600 to-indigo-600 py-8 text-center">
        <h1 className="text-2xl font-bold mb-1 text-white">Python Syllabus</h1>
        <p className="text-sm text-white">
          Master Python Programming (30 Hours – 1 Hour Per Day)
        </p>
      </header>

      {/* Training Availability Banner */}
      <div className="fixed top-34 left-80v right-4 bg-yellow-200 border border-yellow-400 rounded-lg p-3 text-center shadow-md z-10">
        <h2 className="text-sm font-semibold text-indigo-800">
          Training Available
        </h2>
        <p className="text-xs text-gray-700">Online Class | Physical Class</p>
        <button
          onClick={handleEnroll}
          className="mt-2 px-4 py-1 bg-orange-600 text-white text-xs rounded-lg hover:bg-orange-700 transition duration-300"
        >
          Enroll Now
        </button>
      </div>

      <div className="max-w-4xl mx-auto p-2 pt-16 pb-16">
        {/* Course Overview */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("courseOverview")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.courseOverview.title}
            <span>{expandedSections["courseOverview"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["courseOverview"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.courseOverview.content}
            </div>
          )}
        </div>

        {/* Daily Schedule */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("dailySchedule")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.dailySchedule.title}
            <span>{expandedSections["dailySchedule"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["dailySchedule"] && (
            <div className="p-1 bg-white">
              {syllabusData.dailySchedule.days.map((day, index) => (
                <div
                  key={index}
                  className="mb-1 border border-gray-100 rounded-lg"
                >
                  <h3
                    onClick={() => toggleSection(`day${index}`)}
                    className="bg-gray-50 p-1 cursor-pointer flex justify-between items-center text-xs font-medium text-gray-600 hover:bg-gray-100"
                  >
                    {day.day}
                    <span>{expandedSections[`day${index}`] ? "▲" : "▼"}</span>
                  </h3>
                  {expandedSections[`day${index}`] && (
                    <div className="p-1 bg-white text-gray-600 leading-tight">
                      <p className="font-bold text-sm">Objective:</p>{" "}
                      {day.objective}
                      {day.topics}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Learning Outcomes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("learningOutcomes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.learningOutcomes.title}
            <span>{expandedSections["learningOutcomes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["learningOutcomes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.learningOutcomes.content}
            </div>
          )}
        </div>

        {/* Final Project Ideas */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("finalProjectIdeas")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.finalProjectIdeas.title}
            <span>{expandedSections["finalProjectIdeas"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["finalProjectIdeas"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.finalProjectIdeas.content}
            </div>
          )}
        </div>

        {/* Additional Notes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("additionalNotes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.additionalNotes.title}
            <span>{expandedSections["additionalNotes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["additionalNotes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.additionalNotes.content}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Python;
